import React from 'react'
import styled from 'styled-components'

import { Button } from '../Button'
import { MediaQuery } from '../constants'
import useKeypress, { Keys } from '../hooks/useKeyPress'
import isIsSSR from '../hooks/useIsSSR'

const { screenSm } = MediaQuery

const _window = typeof window !== 'undefined' ? window : null

export type ModalCloseResult = { closedBy?: 'bg' | 'esc' | 'click' }

export type ModalProps = {
  children?: React.ReactNode
  open?: boolean
  onClose?(result?: ModalCloseResult): void
  closeBtn?: boolean
  disableBgClose?: boolean
  disableEscClose?: boolean
  containerSyle?: React.CSSProperties
}

const Backdrop = styled.div<{ visible: boolean }>`
  position: fixed;
  z-index: 101;
  background-color: var(--dropdown-backdrop);
  opacity: ${({ visible }) => (visible ? '1' : '0')};
  pointer-events: ${({ visible }) => (visible ? 'all' : 'none')};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: opacity 200ms ease;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`

const ModalContainer = styled.div<{ visible: boolean }>`
  background: var(--surface-primary);
  border-radius: var(--border-radius-xs);
  padding: 30px;
  opacity: ${({ visible }) => (visible ? '1' : '0')};
  transform: scale(${({ visible }) => (visible ? 1 : 0.92)});
  transition: all 100ms ease;
  width: 600px;
  position: relative;
  top: 15%;

  @media (max-width: ${screenSm}) {
    padding: 20px;
    margin: 15px;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: calc(100% - 30px);
  }

  :focus {
    outline: none;
  }
`

const ModalClose = styled.div`
  position: absolute;
  top: 5px;
  right: 20px;
`

const Modal = ({
  children,
  open = false,
  onClose,
  closeBtn,
  disableBgClose,
  disableEscClose,
  containerSyle,
}: ModalProps) => {
  const isSSR = isIsSSR()
  const [isBackdropVisible, setIsBackdropVisible] = React.useState(false)
  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const close = (result: ModalCloseResult) => {
    if (result.closedBy === 'bg' && disableBgClose) return
    if (result.closedBy === 'esc' && disableEscClose) return

    onClose?.(result)
  }

  useKeypress(Keys.ESCAPE, (e: KeyboardEvent) => {
    // istanbul ignore next
    e.preventDefault()

    close({
      closedBy: 'esc',
    })
  })

  React.useEffect(() => {
    if (open) {
      setIsBackdropVisible(true)

      if (isSSR) {
        setIsModalVisible(true)
      } else {
        setTimeout(() => {
          // istanbul ignore next
          setIsModalVisible(true)
        }, 400)
      }

      if (_window) _window.document.body.style.overflow = 'hidden'
    } else {
      setIsModalVisible(false)
      setIsBackdropVisible(false)

      if (_window) _window.document.body.style.overflow = 'visible'
    }

    return () => {
      if (_window) _window.document.body.style.overflow = 'visible'
    }
  }, [open])

  return (
    <>
      <Backdrop
        visible={isBackdropVisible}
        onClick={() => close({ closedBy: 'bg' })}
        data-testid='modal-backdrop'
        tabIndex={-1}
        aria-modal='true'
        aria-label='Modal backdrop'
        role='dialog'
      >
        <ModalContainer
          visible={isModalVisible}
          onClick={(e) => {
            e.stopPropagation()
          }}
          style={containerSyle}
          aria-modal='true'
          aria-label='Modal'
          role='dialog'
          tabIndex={-1}
          data-testid='modal-container'
        >
          {closeBtn ? (
            <ModalClose>
              <Button
                style={{ border: 'none', background: 'none', padding: 0 }}
                icon='CLOSE_X'
                iconSide='right'
                onClick={(e) => {
                  e.preventDefault()

                  close({ closedBy: 'click' })
                }}
                aria-label='Close Modal'
              />
            </ModalClose>
          ) : null}
          {children}
        </ModalContainer>
      </Backdrop>
    </>
  )
}

export const ModalContentWithButton = ({
  children,
  onClose,
}: {
  children: React.ReactNode
  onClose: ModalProps['onClose']
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          padding: '30px',
          minHeight: '200px',
          maxHeight: '600px',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'auto',
          padding: '20px',
          borderTop: '1px solid var(--gray200)',
        }}
      >
        <Button
          style={{ width: '100px' }}
          onClick={(e) => {
            e.preventDefault()

            onClose?.({ closedBy: 'click' })
          }}
        >
          Close
        </Button>
      </div>
    </div>
  )
}

export default Modal
